import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import {
  distinctUntilChanged,
  map,
  shareReplay,
  skip,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { AbstractControl, UntypedFormControl, UntypedFormGroup, ValidationErrors } from '@angular/forms';
import { OrderformFascade } from '../../fascades/orderform.fascade';
import { firstValueFrom, Subject, Subscription } from 'rxjs';
import { OrderformPaymentPlanItem, OrderFormValidationResult } from '@orderform/widgets';
import { isPageBuilderDesignMode } from '../../misc/pagebuilder';
import { getInvalidFieldListFromFormGroup } from '../../misc/get-invalid-field-list-from-form-group';
import { OrderformService } from '../../services/orderform.service';

@Component({
  selector: 'orderform-payment-plans',
  templateUrl: './payment-plans.component.html',
  styleUrls: ['./payment-plans.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PaymentPlansComponent implements OnInit, OnDestroy {
  private _isBruttoPriceEnforced$ = new Subject<string>();

  @Input() set is_brutto_price_enforced(v: string) {
    this._isBruttoPriceEnforced$.next(v);
  }

  @Output() readonly validate = new EventEmitter<{
    valid: boolean;
    source: 'change' | 'submit';
  }>();

  private destroy$ = new Subject<void>();

  PGB_VIEW_MODE = isPageBuilderDesignMode();

  private _saveFormGroupValueChange: Subscription;
  private _subscriptionFormValidation = this._orderformFascade.formValidation.subscribe(
    () => this._onBeforeSubmit(),
    this._element
  );

  canPreselectPlan$ = this._orderformFascade.config$.pipe(map((config) => config.can_preselect_payplan));
  order$ = this._orderformFascade.order$.pipe(shareReplay());
  paymentPlanId$ = this._orderformFascade.paymentPlanId$;
  headline$ = this.order$.pipe(map((e) => e.payment_plans_headline));
  plans$ = this.order$.pipe(map((e) => e.payment_plans));

  private _isValueValid = (payment_plan_id: string | number | null) => {
    /**
     * Check is not null or undefined
     * allow 0 because it is a number
     * allow "0"
     */
    return !!payment_plan_id || Number.isInteger(payment_plan_id) ? Number.isInteger(+payment_plan_id) : false;
  };

  formGroup = new UntypedFormGroup({
    plan_id: new UntypedFormControl(null, (control: AbstractControl): ValidationErrors | null => {
      const isValueValid = this._isValueValid(control.value);
      return isValueValid ? null : { selectPaymentPlan: { value: control.value } };
    }),
  });

  private _formValidationSubscription = this.formGroup.statusChanges
    .pipe(
      map(() => {
        return this.formGroup.valid;
      })
    )
    .subscribe((valid) => this.validate.emit({ valid, source: 'change' }));

  private _submitFailedSubscription = this._orderformFascade.submitFailed.subscribe(() => {
    const valid = this.formGroup.valid;
    this.validate.emit({ valid, source: 'submit' });
  });

  formGroup$ = this.paymentPlanId$.pipe(
    distinctUntilChanged(),
    tap(() => {
      // unsubscribe when new formGroup generates
      if (this._saveFormGroupValueChange) {
        this._saveFormGroupValueChange.unsubscribe();
      }
    }),
    map((payment_plan_id) => {
      this.formGroup.get('plan_id').setValue(this._isValueValid(payment_plan_id) ? +payment_plan_id : null);
      return this.formGroup;
    }),
    tap((formGroup) => {
      this._saveFormGroupValueChange = formGroup.valueChanges
        .pipe(
          // take plan from formGroup
          map((values) => values.plan_id),
          takeUntil(this.destroy$)
        )
        .subscribe((planId) => {
          // set payment plan to store
          this._orderformFascade.setOrderStore({
            payment_plan_id: planId,
          });
          firstValueFrom(this._orderformFascade.update$());
        });
    }),
    shareReplay(1)
  );

  trackByPlanId = (i, value: OrderformPaymentPlanItem) => value.payment_plan_id;

  constructor(
    private _orderformFascade: OrderformFascade,
    private _cdr: ChangeDetectorRef,
    private _element: ElementRef<HTMLElement>,
    private orderformService: OrderformService
  ) {
    this._isBruttoPriceEnforced$.pipe(takeUntil(this.destroy$), skip(1)).subscribe((enforced) => {
      this._orderformFascade.setOrderStore({
        payment_plan_is_brutto_price_enforced: enforced,
      });
      firstValueFrom(this._orderformFascade.update$());
    });

    /**
     * Listen on can preselect to remove
     * preselection for payment plans (eg change country from de to us)
     */
    this.canPreselectPlan$
      .pipe(
        distinctUntilChanged(),
        skip(1), // Skip first emit of canPreselect
        switchMap((canPreselect) => {
          return this._orderformFascade.selectedPlan$.pipe(
            distinctUntilChanged((a, b) => a.payment_plan_id === b.payment_plan_id),
            map((selectedPlan) => ({
              selectedPlanId: selectedPlan.payment_plan_id,
              canPreselect,
            }))
          );
        })
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        /**
         * Der Nutzer muss die Bezahlpläne manuell erneut setzten
         * sonst kann es passieren, dass die Checkboxen vorausgewählt sind (DE => USA)
         */
        this._orderformFascade.setOrderStore({
          payment_plan_id: data.canPreselect ? data.selectedPlanId : null,
        });
      });
  }

  ngOnInit(): void {
    this.orderformService.paymentPlanError$?.pipe(takeUntil(this.destroy$)).subscribe((error) => {
      if (error) {
        this.formGroup.get('plan_id').setValue(null);
        this.formGroup.get('plan_id').setErrors({ required: true });
        this.formGroup.get('plan_id').markAsDirty();
        this.formGroup.markAllAsTouched();
      }
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this._subscriptionFormValidation.unsubscribe();
    this._formValidationSubscription.unsubscribe();
    this._submitFailedSubscription.unsubscribe();
  }

  private async _onBeforeSubmit(): Promise<OrderFormValidationResult> {
    this.formGroup.get('plan_id').markAsDirty();
    this.formGroup.markAllAsTouched();
    this._cdr.detectChanges();
    this.formGroup.updateValueAndValidity({ emitEvent: false });
    const plans = await firstValueFrom(this.plans$);
    return Promise.resolve({
      valid: plans.length === 0 || this.formGroup.valid,
      component: 'ds-orderform-payment-plans',
      invalidFieldList: getInvalidFieldListFromFormGroup(this.formGroup),
    });
  }
}
